import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calculator, Percent, ShieldCheck, TrendingDown, HelpCircle } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import SEO from '@/components/common/SEO'
import { ROUTES } from '@/lib/constants'
import PromotionSlot from '@/components/common/PromotionSlot'

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

export default function GuideCostPage() {
  const navigate = useNavigate()

  return (
    <>
      <SEO
        title="요양 비용 가이드"
        description="요양시설 비용 구조를 한눈에 알아보세요. 등급별 본인부담금, 급여·비급여 항목, 감경 제도까지 쉽게 설명합니다."
        url="/guide/cost"
      />
      <SubpageHero title="요양 비용 가이드" description="등급별 비용 구조와 감경 제도 안내" icon={<Calculator size={20} className="text-white" />} />
      <PageContainer>
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {/* 소개 */}
          <motion.section variants={fadeUp}>
            <h1 className="text-h2 font-bold text-cb-gray-900">요양 비용, 얼마나 들까요?</h1>
            <p className="mt-2 text-body text-cb-gray-600 leading-relaxed">
              요양시설 비용은 장기요양등급, 시설 유형, 감경 여부에 따라 크게 달라집니다.
              비용 구조를 이해하면 합리적인 선택을 할 수 있습니다.
            </p>
          </motion.section>

          {/* 비용 구조 개요 */}
          <motion.section variants={fadeUp}>
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100">
                  <Calculator size={22} className="text-cb-primary" />
                </div>
                <div>
                  <h2 className="text-body font-bold text-cb-gray-900">비용 구조 이해하기</h2>
                  <p className="text-caption text-cb-gray-500">총 비용 = 본인부담금 + 비급여 항목</p>
                </div>
              </div>
              <div className="flex flex-col gap-3 rounded-xl bg-cb-gray-50 p-4 text-body-sm text-cb-gray-700 leading-relaxed">
                <div>
                  <span className="font-semibold text-cb-primary">급여 항목 (장기요양보험 적용)</span>
                  <p className="mt-1">요양원(시설급여)은 비용의 80%를 국민건강보험공단이 부담하고 본인이 <strong>20%</strong>를 냅니다. 방문요양·주간보호(재가급여)는 본인부담이 <strong>15%</strong>이고, 등급별 월 한도액 안에서 이용합니다.</p>
                </div>
                <div>
                  <span className="font-semibold text-cb-accent">비급여 항목 (전액 본인부담)</span>
                  <p className="mt-1">식대, 이미용비, 상급침실 이용료, 간식비 등은 장기요양보험이 적용되지 않아 전액 본인이 부담합니다. 감경 대상자도 마찬가지이며, 시설마다 금액이 다릅니다.</p>
                </div>
              </div>
            </Card>
          </motion.section>

          {/* 등급별 월 한도액 */}
          <motion.section variants={fadeUp}>
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100">
                  <Percent size={22} className="text-cb-accent" />
                </div>
                <h2 className="text-body font-bold text-cb-gray-900">등급별 재가급여 월 한도액 (2026년 기준)</h2>
              </div>
              <Card noPadding className="overflow-x-auto border border-cb-gray-100">
                <table className="w-full text-caption min-w-[400px]">
                  <thead>
                    <tr className="bg-cb-gray-50">
                      <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">등급</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-cb-gray-600">월 한도액</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-cb-gray-600">본인부담금 (15%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">1등급</td><td className="px-3 py-2.5 text-right">2,512,900원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 376,900원</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">2등급</td><td className="px-3 py-2.5 text-right">2,331,200원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 349,600원</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">3등급</td><td className="px-3 py-2.5 text-right">1,528,200원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 229,200원</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">4등급</td><td className="px-3 py-2.5 text-right">1,409,700원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 211,400원</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">5등급</td><td className="px-3 py-2.5 text-right">1,208,900원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 181,300원</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">인지지원등급</td><td className="px-3 py-2.5 text-right">676,320원</td><td className="px-3 py-2.5 text-right font-semibold text-cb-primary">약 101,400원</td></tr>
                  </tbody>
                </table>
              </Card>
              <p className="mt-3 text-[11px] text-cb-gray-400">* 방문요양·주야간보호 등 재가급여 기준입니다. 시설급여(요양원)는 월 한도액이 없고 이용 일수로 산정하며, 본인부담률은 20%입니다.</p>
            </Card>
          </motion.section>

          {/* 시설 유형별 비용 비교 */}
          <motion.section variants={fadeUp}>
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100">
                  <TrendingDown size={22} className="text-cb-secondary" />
                </div>
                <h2 className="text-body font-bold text-cb-gray-900">시설 유형별 월 예상 비용</h2>
              </div>
              <div className="flex flex-col gap-2.5">
                {[
                  { type: '요양원 (입소)', range: '50~180만원', desc: '본인부담금 + 식대 + 간식비 + 이미용비 등', color: 'bg-blue-500' },
                  { type: '주간보호 (통원)', range: '30~80만원', desc: '본인부담금 + 식대 + 송영비', color: 'bg-amber-500' },
                  { type: '방문요양 (자택)', range: '20~60만원', desc: '본인부담금 (이용시간에 따라)', color: 'bg-green-500' },
                  { type: '방문목욕', range: '회당 약 1.5만원', desc: '본인부담금 (주 1~2회)', color: 'bg-cyan-500' },
                  { type: '방문간호', range: '회당 약 1만원', desc: '본인부담금 (방문 횟수에 따라)', color: 'bg-rose-500' },
                ].map((item) => (
                  <div key={item.type} className="flex items-center gap-3 rounded-xl bg-cb-gray-50 px-3 py-3">
                    <div className={`h-2.5 w-2.5 rounded-full ${item.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-caption font-semibold text-cb-gray-800 truncate">{item.type}</span>
                        <span className="text-caption font-bold text-cb-primary flex-shrink-0">{item.range}</span>
                      </div>
                      <p className="text-[11px] text-cb-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-cb-gray-400">* 3등급 기준 예시이며, 등급·감경·지역에 따라 달라질 수 있습니다.</p>
            </Card>
          </motion.section>

          {/* 감경 제도 */}
          <motion.section variants={fadeUp}>
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100">
                  <ShieldCheck size={22} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-body font-bold text-cb-gray-900">본인부담금 감경 제도</h2>
                  <p className="text-caption text-cb-gray-500">소득 수준에 따라 부담이 줄어듭니다</p>
                </div>
              </div>
              <Card noPadding className="overflow-x-auto border border-cb-gray-100">
                {/* 공식 감경 기준 (확인 2026-10-14) — 구현지침 docs/develop/domain/cost/비용_시뮬레이터_구현_지침.md §7.1 */}
                <table className="w-full text-caption min-w-[400px]">
                  <thead>
                    <tr className="bg-cb-gray-50">
                      <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">대상</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-cb-gray-600">감경</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-cb-gray-600">요양원</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-cb-gray-600">재가</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5">기초생활수급자 (의료급여 수급)</td><td className="px-3 py-2.5 text-right font-semibold text-green-600">면제</td><td className="px-3 py-2.5 text-right">0%</td><td className="px-3 py-2.5 text-right">0%</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5">기초생활수급자가 아닌 의료급여 수급자(국가유공자·북한이탈주민 등), 병원비 경감을 받는 차상위계층, 재난 등 생계곤란, 건강보험료 순위 하위 25% 이하</td><td className="px-3 py-2.5 text-right font-semibold text-blue-600">60%</td><td className="px-3 py-2.5 text-right">8%</td><td className="px-3 py-2.5 text-right">6%</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5">건강보험료 순위 하위 25% 초과~50% 이하</td><td className="px-3 py-2.5 text-right font-semibold text-blue-600">40%</td><td className="px-3 py-2.5 text-right">12%</td><td className="px-3 py-2.5 text-right">9%</td></tr>
                    <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5">일반 (감경 없음)</td><td className="px-3 py-2.5 text-right text-cb-gray-400">-</td><td className="px-3 py-2.5 text-right">20%</td><td className="px-3 py-2.5 text-right">15%</td></tr>
                  </tbody>
                </table>
              </Card>
              <ul className="mt-3 flex flex-col gap-1 text-[11px] text-cb-gray-400">
                <li>* 재가는 방문요양·주간보호 등 재가급여입니다. 직장가입자는 재산 기준도 충족해야 감경됩니다.</li>
                <li>* 감경은 대부분 공단이 직권으로 적용합니다. 보험료가 바뀌어 새로 해당되면 공단 운영센터에 신청하고, 14일 이내에 결과를 통보받습니다.</li>
                <li>* 식재료비·상급침실료·이미용비 같은 비급여는 감경 대상자도 전액 본인부담입니다. 문의: 국민건강보험공단 1577-1000</li>
              </ul>
            </Card>
          </motion.section>

          {/* 자주 묻는 질문 */}
          <motion.section variants={fadeUp}>
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100">
                  <HelpCircle size={22} className="text-sky-600" />
                </div>
                <h2 className="text-body font-bold text-cb-gray-900">자주 묻는 질문</h2>
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-body-sm font-semibold text-cb-gray-800">Q. 장기요양등급이 없어도 시설을 이용할 수 있나요?</p>
                  <p className="mt-1 text-caption text-cb-gray-600 leading-relaxed">등급 없이도 이용할 수 있지만, 건강보험 혜택을 받을 수 없어 전액 본인부담입니다. 등급 신청은 국민건강보험공단(1577-1000)에서 할 수 있습니다.</p>
                </div>
                <div>
                  <p className="text-body-sm font-semibold text-cb-gray-800">Q. 요양원과 요양병원은 비용이 다른가요?</p>
                  <p className="mt-1 text-caption text-cb-gray-600 leading-relaxed">네, 다릅니다. 요양원은 장기요양보험이, 요양병원은 건강보험(의료보험)이 적용됩니다. 요양병원은 의료 비중이 높아 비용 구조가 다르며, 일반적으로 요양병원이 더 비쌉니다.</p>
                </div>
                <div>
                  <p className="text-body-sm font-semibold text-cb-gray-800">Q. 비급여 항목은 어떤 것이 있나요?</p>
                  <p className="mt-1 text-caption text-cb-gray-600 leading-relaxed">식대(월 20~30만원), 간식비, 이미용비, 상급침실 이용료, 프로그램 재료비 등이 있습니다. 시설마다 항목과 금액이 다르므로 입소 전 반드시 확인하세요.</p>
                </div>
                <div>
                  <p className="text-body-sm font-semibold text-cb-gray-800">Q. 두 가지 서비스를 동시에 이용할 수 있나요?</p>
                  <p className="mt-1 text-caption text-cb-gray-600 leading-relaxed">재가급여(방문요양, 주간보호 등)는 월 한도액 내에서 여러 서비스를 조합해 이용할 수 있습니다. 다만 시설급여(요양원 입소) 중에는 재가급여를 동시에 이용할 수 없습니다.</p>
                </div>
              </div>
            </Card>
          </motion.section>

          {/* CTA */}
          <motion.section variants={fadeUp} className="flex flex-col gap-3 pb-4">
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ROUTES.COST_SIMULATOR)}>
              내 비용 직접 계산해보기
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(ROUTES.FACILITIES)}>
              내 근처 요양시설 찾아보기
            </Button>
          </motion.section>

          {/* 프로모션 슬롯: guide_bottom */}
          <PromotionSlot slotKey="guide_bottom" className="mt-2" />
        </motion.div>
      </PageContainer>
    </>
  )
}
